import React, { useMemo } from 'react';
import { Modal, View, Text, StyleSheet, TouchableOpacity, ScrollView, useWindowDimensions, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { BRAND, useManagementTheme } from '../../helpers/managementTheme';
import { WINDOW_MEDIUM_MIN, getModalWidth } from '../../helpers/windowSizeClass';
import { getDialogStrings, getModuleLabel } from './moduleWhatsNewStrings';

/** Map app language codes (incl. non-standard "cn"/"svn") to BCP-47 for Intl date formatting. */
const DATE_LOCALE_MAP = { en: 'en-US', hu: 'hu-HU', de: 'de-DE', cn: 'zh-CN', ro: 'ro-RO', svn: 'sl-SI' };

/**
 * Resolve a localized string from an `{ en, hu, ... }` map with an English fallback.
 * @param {Object|string} field Localized map or plain string.
 * @param {string} lang Active language.
 * @returns {string}
 */
const localizeField = (field, lang) => {
  if (!field) {
    return '';
  }
  if (typeof field === 'string') {
    return field;
  }
  const base = (lang || 'en').split('-')[0].toLowerCase();

  return field[base] || field.en || Object.values(field).find(Boolean) || '';
};

/**
 * @param {string} dateStr ISO date.
 * @param {string} lang Active language.
 * @returns {string}
 */
const formatDate = (dateStr, lang) => {
  const parsed = new Date(dateStr);

  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  try {
    return parsed.toLocaleDateString(DATE_LOCALE_MAP[(lang || 'hu').split('-')[0].toLowerCase()] || 'hu-HU', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch (_e) {
    return parsed.toLocaleDateString();
  }
};

/**
 * ModuleWhatsNewDialog — premium modal for one module's unseen What's New entries. Same solid
 * white / navy card (radius 28) with the orange badge and pill primary button as the global
 * WhatsNewModal. Presentational: the mount owns visibility and seen-state.
 *
 * @param {object} props
 * @param {boolean} props.visible Whether the modal is shown.
 * @param {string} props.moduleKey Module key (drives the localized title).
 * @param {Object[]} props.entries Entries to list.
 * @param {Function} props.onClose Close / "Got it" (the mount marks the entries seen).
 * @param {Function} [props.onSeeAll] Optional: close and open the global What's New.
 * @returns {JSX.Element}
 */
const ModuleWhatsNewDialog = ({ visible, moduleKey, entries, onClose, onSeeAll }) => {
  const { i18n } = useTranslation();
  const m = useManagementTheme();
  const styles = useMemo(() => StyleSheet.create(buildStyles(m)), [m]);
  const { width, height } = useWindowDimensions();
  const lang = i18n.language || 'hu';
  const dict = getDialogStrings(lang);
  const title = dict.title.replace('{{module}}', getModuleLabel(moduleKey, lang));
  const modalWidth = width >= WINDOW_MEDIUM_MIN ? getModalWidth(width, { max: 700 }) : width * 0.95;

  return (
    <Modal animationType="fade" transparent visible={visible} onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={[styles.modalBox, { width: modalWidth, maxHeight: height * 0.85 }]}>
          <View style={styles.header}>
            <View style={styles.headerRow}>
              <View style={styles.badge}>
                <Ionicons name="sparkles-outline" size={24} color="#FFF" />
              </View>
              <View style={styles.headerText}>
                <Text style={styles.kicker}>{dict.kicker.toUpperCase()}</Text>
                <Text style={styles.titleText}>{title}</Text>
                <Text style={styles.subtitleText}>{dict.subtitle}</Text>
              </View>
              <TouchableOpacity onPress={onClose} style={styles.closeBtn} accessibilityLabel={dict.close}>
                <Ionicons name="close" size={24} color={m.colors.subText} />
              </TouchableOpacity>
            </View>
          </View>

          <ScrollView style={styles.scrollBody} contentContainerStyle={styles.scrollContent}>
            {(Array.isArray(entries) ? entries : []).map((entry) => {
              const date = formatDate(entry.date, lang);

              return (
                <View key={entry.id} style={styles.card}>
                  <View style={styles.cardTop}>
                    <View style={styles.dot} />
                    <Text style={styles.entryTitle}>{localizeField(entry.title, lang)}</Text>
                  </View>
                  {date ? <Text style={styles.entryDate}>{date}</Text> : null}
                  <Text style={styles.entryBody}>{localizeField(entry.body, lang)}</Text>
                </View>
              );
            })}
          </ScrollView>

          <View style={styles.footer}>
            {typeof onSeeAll === 'function' ? (
              <TouchableOpacity onPress={onSeeAll} style={styles.ghostBtn}>
                <Text style={styles.ghostBtnText}>{dict.see_all}</Text>
              </TouchableOpacity>
            ) : <View />}
            <TouchableOpacity onPress={onClose} style={styles.gotItBtn}>
              <Text style={styles.gotItBtnText}>{dict.got_it}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

/**
 * Style factory bound to the management theme (light + navy dark).
 * @param {object} m Result of `useManagementTheme()`.
 * @returns {Object.<string, object>} Style map.
 */
const buildStyles = (m) => ({
  backdrop: { flex: 1, backgroundColor: 'rgba(15,23,42,0.55)', justifyContent: 'center', alignItems: 'center' },
  modalBox: {
    backgroundColor: m.colors.surface,
    borderRadius: 28,
    borderWidth: 1,
    borderColor: m.colors.border,
    overflow: 'hidden',
    ...Platform.select({
      ios: { shadowColor: '#0F172A', shadowOffset: { width: 0, height: 12 }, shadowOpacity: 0.25, shadowRadius: 24 },
      android: { elevation: 12 },
      web: { boxShadow: '0px 18px 40px rgba(15,23,42,0.25)' },
    }),
  },
  header: { padding: 18, borderBottomWidth: 1, borderBottomColor: m.colors.border, backgroundColor: m.colors.surface },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  badge: { width: 46, height: 46, borderRadius: 16, backgroundColor: BRAND.base, justifyContent: 'center', alignItems: 'center' },
  headerText: { flex: 1, marginHorizontal: 12 },
  kicker: { fontSize: 10.5, fontWeight: '800', letterSpacing: 1, color: BRAND.base, marginBottom: 2 },
  titleText: { fontSize: 20, fontWeight: '800', letterSpacing: -0.4, color: m.colors.heading },
  subtitleText: { fontSize: 12.5, color: m.colors.subText, marginTop: 2 },
  closeBtn: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: m.colors.surfaceAlt },
  scrollBody: { backgroundColor: m.colors.surface },
  scrollContent: { padding: 16 },
  card: { marginBottom: 12, borderWidth: 1, borderColor: m.colors.border, borderRadius: 22, backgroundColor: m.colors.surfaceAlt, padding: 14 },
  cardTop: { flexDirection: 'row', alignItems: 'flex-start' },
  dot: { width: 7, height: 7, borderRadius: 4, marginTop: 7, marginRight: 8, backgroundColor: BRAND.base },
  entryTitle: { flex: 1, fontSize: 15, fontWeight: '800', color: m.colors.heading, lineHeight: 21 },
  entryDate: { fontSize: 11, color: m.colors.muted, marginTop: 4, marginLeft: 15 },
  entryBody: { fontSize: 13, color: m.colors.subText, lineHeight: 19, marginTop: 8 },
  footer: {
    padding: 14,
    borderTopWidth: 1,
    borderTopColor: m.colors.border,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: m.colors.surface,
  },
  ghostBtn: { paddingHorizontal: 18, height: 46, borderRadius: 999, borderWidth: 1, borderColor: m.colors.border, alignItems: 'center', justifyContent: 'center' },
  ghostBtnText: { color: m.colors.subText, fontWeight: '700', fontSize: 14 },
  gotItBtn: { backgroundColor: BRAND.base, paddingHorizontal: 26, height: 46, borderRadius: 999, alignItems: 'center', justifyContent: 'center' },
  gotItBtnText: { color: '#FFFFFF', fontWeight: '800', fontSize: 14.5 },
});

export default ModuleWhatsNewDialog;
